'use client';

import Link from 'next/link';
import Image from 'next/image';
import { useState, useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { Menu, X } from 'lucide-react';
import { getConsumerUrl } from '@/lib/urls';
import { DynamicLink } from './DynamicLink';

const navLinks = [
  { href: '/', label: 'Home' },
  { href: '/voor-wie', label: 'Voor Wie' },
  { href: '/pakketten', label: 'Pakketten' },
  { href: '/platform', label: 'Platform' },
  { href: '/gemeenten', label: 'Gemeenten' },
  { href: '/blog', label: 'Blog' },
];

export function ProfessionalHeader() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  // Only show transparent header on homepage
  const isHomepage = pathname === '/' || pathname === '/professionals';

  useEffect(() => {
    const handleScroll = () => {
      // Change header style after scrolling just 50px
      setScrolled(window.scrollY > 50);
    };

    // Run once on mount to check initial scroll position
    handleScroll();

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // On subpages, always use scrolled (solid) style
  const useScrolledStyle = scrolled || !isHomepage;

  return (
    <header
      className={`fixed z-50 transition-all duration-300 ${
        useScrolledStyle
          ? 'top-0 left-0 right-0 bg-white shadow-md border-b border-gray-100'
          : 'top-4 md:top-6 lg:top-8 left-4 md:left-6 lg:left-8 right-4 md:right-6 lg:right-8 bg-transparent'
      }`}
    >
      <div className={`${useScrolledStyle ? 'container-site' : 'px-4 md:px-8'}`}>
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3">
            <Image
              src="/images/logo.svg"
              alt="ValBewust"
              width={200}
              height={45}
              className={`h-10 w-auto transition-all duration-300 ${
                useScrolledStyle ? '' : 'brightness-0 invert'
              }`}
              priority
            />
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full hidden sm:inline ${
              useScrolledStyle
                ? 'text-cta bg-cta/15 border border-cta/30'
                : 'text-yellow-400 bg-yellow-400/20 border border-yellow-400/40'
            }`}>
              Zakelijk
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-6">
            {navLinks.map((link) => {
              const isActive = link.href === '/'
                ? pathname === '/' || pathname === '/professionals'
                : pathname.startsWith(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  className={`font-medium transition-colors ${
                    isActive
                      ? useScrolledStyle
                        ? 'text-cta'
                        : 'text-yellow-400'
                      : useScrolledStyle
                        ? 'text-ink hover:text-cta'
                        : 'text-white/90 hover:text-white'
                  }`}
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-4">
            <DynamicLink
              getHref={() => getConsumerUrl()}
              className={`text-sm font-medium px-3 py-2 rounded-full transition-all whitespace-nowrap ${
                useScrolledStyle
                  ? 'text-ink/70 hover:text-cta'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              Voor Consumenten
            </DynamicLink>
            <Link
              href="/contact"
              className={`inline-flex items-center justify-center px-5 py-2.5 rounded-full font-bold text-sm transition-all duration-300 hover:scale-105 whitespace-nowrap ${
                useScrolledStyle
                  ? 'bg-cta text-white hover:bg-cta-dark'
                  : 'bg-white text-ink hover:bg-cta hover:text-white'
              }`}
            >
              Contact
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`lg:hidden p-2 ${useScrolledStyle ? 'text-ink' : 'text-white'}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-6 bg-white rounded-2xl mt-2 shadow-xl">
            <nav className="flex flex-col gap-1 px-4">
              {navLinks.map((link) => {
                const isActive = link.href === '/'
                  ? pathname === '/' || pathname === '/professionals'
                  : pathname.startsWith(link.href);
                return (
                  <Link
                    key={link.href}
                    href={link.href}
                    className={`transition-colors py-3 px-4 rounded-xl font-medium ${
                      isActive
                        ? 'text-cta bg-cta/10'
                        : 'text-ink/70 hover:text-ink hover:bg-gray-50'
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {link.label}
                  </Link>
                );
              })}
              <hr className="my-3 border-gray-100" />
              <DynamicLink
                getHref={() => getConsumerUrl()}
                className="text-cta hover:text-cta-dark transition-colors py-3 px-4 font-medium"
                onClick={() => setMobileMenuOpen(false)}
              >
                Voor Consumenten →
              </DynamicLink>
              <Link
                href="/contact"
                className="bg-cta text-white text-center py-4 px-6 rounded-full font-bold mt-2 hover:bg-cta-dark transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                Neem Contact Op
              </Link>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
